/**
 * 慣性スクロール（Lenis）。
 * - PC（≥1025px）かつ reduced-motion off のときだけ有効化。SP/低モーションはネイティブスクロール維持。
 * - PCホイールのみ慣性補間（syncTouch:false＝タッチ端末はネイティブ）。
 * - ページ内アンカー（#approach 等）は lenis.scrollTo で慣性スクロール。
 * - 既存のscroll連動(story/solution-orbit/service-stack/section-peel)は、Lenisがネイティブの
 *   scroll位置を慣性補間で更新する方式なので window scroll イベントで追従する（改修不要）。
 * 参照: https://github.com/darkroomengineering/lenis
 */
import Lenis from "lenis"

/** 生成済みのLenisインスタンス（PC以外・reduced-motionでは null） */
let lenis: Lenis | null = null

/**
 * Lenisインスタンスを取得（未生成なら null）。
 * splash / cmn-acc などプログラムスクロール側が「あれば lenis.scrollTo / なければ window.scrollTo」で使う。
 * @returns {Lenis | null} 有効時はインスタンス、SP/低モーションでは null
 */
export const getLenis = (): Lenis | null => lenis

/**
 * 初期化。PC・reduced-motion off のときだけ Lenis を起動する。
 */
const initSmoothScroll = (): void => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const isSP = window.matchMedia("(max-width: 1024px)").matches
  if (reduce || isSP) return // SP/低モーションはネイティブスクロールのまま

  lenis = new Lenis({
    lerp: 0.1, // 慣性の強さ（小さいほど長く滑る。0.1が標準的な慣性感）
    smoothWheel: true, // PCホイールを慣性補間
    syncTouch: false, // タッチ端末はネイティブスクロール（iOS慣性を殺さない）
  })

  /**
   * rAFループ：毎フレーム Lenis を進める
   * @param {number} time - rAFのタイムスタンプ
   */
  const raf = (time: number): void => {
    lenis?.raf(time)
    requestAnimationFrame(raf)
  }
  requestAnimationFrame(raf)

  // ページ内アンカー（同一パスの #id）を慣性スクロールで移動（イベント委譲）
  document.addEventListener("click", (e) => {
    if (!lenis) return
    const link = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href*="#"]')
    if (!link) return
    const url = new URL(link.href, location.href)
    if (url.pathname !== location.pathname || !url.hash) return
    const target = document.getElementById(url.hash.slice(1))
    if (!target) return
    e.preventDefault()
    lenis.scrollTo(target)
  })
}

initSmoothScroll()
